<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" @click="close">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- Modal panel -->
      <div class="bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-5xl sm:w-full max-h-[90vh] flex flex-col" style="min-width: 1000px;">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 overflow-y-auto">
          <div class="flex justify-between items-start">
            <div>
              <h3 class="text-xl font-bold text-gray-900">
                Review Forecasting Template
              </h3>
              <p class="text-sm text-gray-500 mt-1">Review and edit the forecast template</p>
            </div>

            <button @click="close" class="text-gray-400 hover:text-gray-500">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Information Card -->
          <div class="mt-4 bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <h4 class="text-sm font-medium text-gray-700">Assignee</h4>
                <div class="flex items-center mt-1">
                  <div v-if="documentData.created_by_avatar_url" class="flex-shrink-0 h-6 w-6">
                    <img :src="documentData.created_by_avatar_url" alt="Avatar" class="h-6 w-6 rounded-full object-cover" />
                  </div>
                  <div v-else class="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-800">
                    {{ documentData.created_by_name ? documentData.created_by_name.charAt(0) : 'U' }}
                  </div>
                  <span class="ml-2 text-sm text-gray-900">{{ documentData.created_by_name || 'Andrej Svetina' }}</span>
                </div>
              </div>

              <div>
                <h4 class="text-sm font-medium text-gray-700">Due Date</h4>
                <p class="mt-1 text-sm text-gray-900">{{ documentData.due_date ? formatDate(new Date(documentData.due_date)) : '17/11/2025' }}</p>
              </div>
            </div>

            <div>
              <h4 class="text-sm font-medium text-gray-700">Description</h4>
              <p class="mt-1 text-sm text-gray-900">{{ documentData.description || 'Q2 2025 marketing budget report for financial planning.' }}</p>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="isFileLoading" class="mt-6 flex items-center justify-center" style="height: 500px;">
            <div class="text-gray-500 flex flex-col items-center">
              <svg class="animate-spin h-10 w-10 text-primary mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p>Loading spreadsheet data...</p>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="fileError" class="mt-6 flex items-center justify-center" style="height: 500px;">
            <div class="text-red-500 flex flex-col items-center">
              <svg class="h-10 w-10 text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p>{{ fileError }}</p>
              <button
                  @click="retryFileLoad"
                  class="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Retry
              </button>
            </div>
          </div>

          <!-- Saving Indicator -->
          <div v-else-if="isSaving" class="mt-6 flex items-center justify-center absolute inset-0 bg-white bg-opacity-75 z-10">
            <div class="text-gray-800 flex flex-col items-center">
              <svg class="animate-spin h-10 w-10 text-primary mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p>Saving changes...</p>
            </div>
          </div>

          <!-- Spreadsheet Container -->
          <div v-else class="relative">

            <!-- Action Buttons -->
            <div class="mt-4 flex space-x-2 p-1">
              <button
                  @click="exportToExcel"
                  class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                </svg>
                Export Excel
              </button>
              <button
                  @click="exportToCsv"
                  class="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                Export CSV
              </button>
              <button
                  @click="saveChangesToDb"
                  class="inline-flex items-center px-3 py-1.5 border border-primary text-sm font-medium rounded-md text-primary bg-white hover:bg-blue-50"
                  :disabled="isSaving"
              >
                <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path>
                </svg>
                Save Changes
              </button>
            </div>

            <div style="height: 500px;">
              <ExcelViewer
                  ref="excelViewerComponent"
                  :fileData="excelFileData || undefined"
                  :readOnly="false"
              />
            </div>

            <!-- Save Success Message -->
            <div v-if="saveSuccess" class="mt-4 p-3 bg-green-100 border border-green-300 rounded-md text-green-700 flex items-center">
              <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              <span>{{ saveSuccess }}</span>
            </div>

            <!-- Save Error Message -->
            <div v-if="saveError" class="mt-4 p-3 bg-red-100 border border-red-300 rounded-md text-red-700 flex items-center">
              <svg class="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <span>{{ saveError }}</span>
            </div>
          </div>
        </div>

        <div class="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end sticky bottom-0">
          <button
              @click="close"
              class="mr-2 inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
          >
            Cancel
          </button>
          <button
              @click="saveAndClose"
              class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
              :disabled="!!isFileLoading || !!fileError || !!isSaving"
          >
            Approve Template
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch } from 'vue';
import ExcelViewer from "@/components/ExcelViewer.vue";
import { logger } from '@/services/logger';
import { DOCUMENT_STATUS } from "@/types/document";
import { supabase } from '@/services/supabase';
import { useSpreadsheetChanges } from '@/composables/useSpreadsheetChanges';

interface ExcelViewerInstance {
  getHotInstance: () => any;
  getWorkbook: () => any;
  exportToExcel: (filename: string) => void;
  exportToCsv: (filename: string) => void;
  getCurrentData: () => { data: any[][]; currentSheet: string } | null;
}

export default defineComponent({
  name: 'SpreadsheetReviewModal',
  components: {
    ExcelViewer
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    documentData: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['close', 'save'],
  setup(props, { emit }) {
    const excelViewerComponent = ref<ExcelViewerInstance | null>(null);
    const isFileLoading = ref(false);
    const fileError = ref<string | null>(null);
    const excelFileData = ref<ArrayBuffer | null>(null);
    const isSaving = ref(false);
    const saveSuccess = ref<string | null>(null);
    const saveError = ref<string | null>(null);
    const fileId = ref<string | null>(null);
    const fileInfo = ref<any>(null);
    // Track if we've already fetched the file to avoid duplicate fetches
    const hasInitiallyFetched = ref(false);

    // Format date for display
    const formatDate = (date: Date): string => {
      return date.toLocaleDateString('de-DE', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    };

    // Format document status for display
    const formatDocumentStatus = (status: DOCUMENT_STATUS): string => {
      switch (status) {
        case DOCUMENT_STATUS.CREATING:
          return 'Creating';
        case DOCUMENT_STATUS.REVIEW_CREATED:
          return 'Review Created';
        case DOCUMENT_STATUS.EMAIL_PENDING:
          return 'Email Pending';
        case DOCUMENT_STATUS.READY_FOR_DOWNLOAD:
          return 'Ready for Download';
        case DOCUMENT_STATUS.DOWNLOADED:
          return 'Downloaded';
        case DOCUMENT_STATUS.UPLOADED_CHECKING:
          return 'Uploaded and Checking';
        case DOCUMENT_STATUS.ADJUSTMENT_NEEDED:
          return 'Adjustment Needed';
        case DOCUMENT_STATUS.REVISION_NEEDED:
          return 'Ready for Revision';
        default:
          return status;
      }
    };

    // We only need the saveChangesToDbComposable function from the composable
    const { saveChangesToDb: saveChangesToDbComposable } = useSpreadsheetChanges();

    // Fetch the Excel file from Supabase
    const fetchExcelFile = async () => {
      fileError.value = null;
      excelFileData.value = null;
      isFileLoading.value = true;
      saveSuccess.value = null;
      saveError.value = null;

      try {
        if (!props.documentData || !props.documentData.id) {
          throw new Error('No document data provided');
        }

        // First check if the document has a file_id
        const documentId = props.documentData.id;

        logger.info('Fetching file for document', { documentId });

        // Query the documents table to get the file_id
        const { data: documentData, error: documentError } = await supabase
            .from('documents')
            .select('file_id')
            .eq('id', documentId)
            .single();

        if (documentError) {
          throw new Error(`Error fetching document: ${documentError.message}`);
        }

        if (!documentData || !documentData.file_id) {
          throw new Error('No file associated with this document');
        }

        fileId.value = documentData.file_id;
        logger.info('Found file_id for document', { documentId, fileId: fileId.value });

        // Now fetch the file record from the files table
        const { data: fileRecord, error: fileError } = await supabase
            .from('files')
            .select('*')
            .eq('id', fileId.value)
            .single();

        if (fileError) {
          throw new Error(`Error fetching file record: ${fileError.message}`);
        }

        if (!fileRecord) {
          throw new Error(`File record not found for ID: ${fileId.value}`);
        }

        fileInfo.value = fileRecord;
        logger.info('File record fetched', { fileRecord });

        // Check if we have a URL to download the file directly
        if (fileRecord.url) {
          try {
            // Attempt to download directly from the URL
            const response = await fetch(fileRecord.url);

            if (!response.ok) {
              throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
            }

            const fileData = await response.arrayBuffer();
            excelFileData.value = fileData;

            logger.info('Excel file loaded successfully via direct URL', {
              documentId,
              fileId: fileId.value,
              url: fileRecord.url
            });
            return;
          } catch (fetchError) {
            logger.warn('Failed to download file via direct URL, falling back to Supabase storage', {
              error: fetchError,
              url: fileRecord.url
            });
            // Continue to the Supabase storage fallback below
          }
        }

        // Extract information from the URL to download from Supabase
        // This is a fallback in case direct download fails
        const urlInfo = extractStorageInfoFromUrl(fileRecord.url);

        if (!urlInfo) {
          throw new Error('Could not determine the storage path from URL');
        }

        logger.info('Extracted storage info', { urlInfo });

        // Download the file from Supabase Storage
        const { data: storageData, error: storageError } = await supabase.storage
            .from(urlInfo.bucket)
            .download(urlInfo.path);

        if (storageError) {
          throw new Error(`Error downloading file from storage: ${storageError.message}`);
        }

        if (!storageData) {
          throw new Error('No file data returned from storage');
        }

        // Convert Blob to ArrayBuffer
        excelFileData.value = await storageData.arrayBuffer();

        logger.info('Excel file loaded successfully from Supabase storage', {
          documentId,
          fileId: fileId.value,
          bucket: urlInfo.bucket,
          path: urlInfo.path
        });
      } catch (error) {
        logger.error('Error fetching Excel file', { error });
        fileError.value = error instanceof Error ? error.message : 'An unknown error occurred';
        excelFileData.value = null;
      } finally {
        isFileLoading.value = false;
      }
    };

    // Helper function to extract bucket and path from Supabase URL
    const extractStorageInfoFromUrl = (url: string) => {
      if (!url) return null;

      try {
        // Parse the URL to extract bucket and path
        // Example URL: https://extravagant.ai-core.dev.azcld.advisori.de/storage/v1/object/public/Templates/1746703577729-1746616064363-EMPTY_2025_responsible_20planners_20report_COM_template_20(3).xlsx

        const regex = /\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+)/;
        const matches = url.match(regex);

        if (matches && matches.length >= 3) {
          return {
            bucket: matches[1],
            path: decodeURIComponent(matches[2])
          };
        }

        return null;
      } catch (error) {
        logger.error('Error parsing storage URL', { url, error });
        return null;
      }
    };

    // Retry loading the file
    const retryFileLoad = () => {
      console.log('Retrying file load');
      fetchExcelFile();
    };

    // Export to Excel
    const exportToExcel = () => {
      if (excelViewerComponent.value) {
        const filename = `${props.documentData.name || 'spreadsheet'}.xlsx`;
        excelViewerComponent.value.exportToExcel(filename);
      }
    };

    // Export to CSV
    const exportToCsv = () => {
      if (excelViewerComponent.value) {
        const filename = `${props.documentData.name || 'spreadsheet'}.csv`;
        excelViewerComponent.value.exportToCsv(filename);
      }
    };

    // Save changes back to the database
    const saveChangesToDb = async () => {
      if (!excelViewerComponent.value || !fileId.value || !fileInfo.value) {
        saveError.value = 'Cannot save: Missing spreadsheet data or file information';
        return;
      }

      // Use the composable to save changes
      const result = await saveChangesToDbComposable(
        excelViewerComponent.value,
        fileId.value,
        fileInfo.value,
        props.documentData.id
      );

      // Update local state based on the result
      if (result.success) {
        // Update component state with the new file information
        fileId.value = result.newFileId || fileId.value;
        fileInfo.value = result.newFileInfo || fileInfo.value;
        excelFileData.value = result.fileData || excelFileData.value;
        saveSuccess.value = result.message;
      } else {
        saveError.value = result.message;
      }
    };

    // Combined watcher for both isOpen and documentData.id
    watch(
      // Watch both values in an array
      [() => props.isOpen, () => props.documentData?.id],
      ([isOpen, documentId]) => {        
        // Only fetch if both conditions are met and we haven't already fetched
        // or if the document ID has changed (which means we need to fetch a new file)
        if (isOpen && documentId && (!hasInitiallyFetched.value || documentId !== fileId.value)) {
          fetchExcelFile();
          hasInitiallyFetched.value = true;
        }
      },
      { immediate: true }
    );

    function close() {
      emit('close');
    }

    function saveAndClose() {
      // Save logic and update document status to EMAIL_PENDING
      logger.info('Saving spreadsheet data and updating document status');

      // First, make sure any changes are saved to the database
      if (excelViewerComponent.value && !isSaving.value) {
        saveChangesToDb().then(() => {
          // Create an updated document object with the new status
          const updatedDocument = {
            ...props.documentData,
            status: DOCUMENT_STATUS.EMAIL_PENDING,
            file_id: fileId.value
          };

          // Log the status change
          logger.info('Document status changed', {
            documentId: props.documentData.id,
            previousStatus: props.documentData.status,
            newStatus: DOCUMENT_STATUS.EMAIL_PENDING
          });

          // Emit save event with the updated document
          emit('save', updatedDocument);

          // Close the modal
          close();
        }).catch((error) => {
          logger.error('Error in saveAndClose', { error });
          saveError.value = error instanceof Error ? error.message : 'An unknown error occurred';
        });
      } else {
        // Just update the status without saving changes
        const updatedDocument = {
          ...props.documentData,
          status: DOCUMENT_STATUS.EMAIL_PENDING
        };

        emit('save', updatedDocument);
        close();
      }
    }

    // When component is mounted - no need to fetch here as the watcher will handle it
    onMounted(() => {
      logger.info('SpreadsheetReviewModal mounted');
    });

    return {
      excelViewerComponent,
      isFileLoading,
      fileError,
      excelFileData,
      isSaving,
      saveSuccess,
      saveError,
      hasInitiallyFetched,
      close,
      saveAndClose,
      formatDate,
      formatDocumentStatus,
      retryFileLoad,
      exportToExcel,
      exportToCsv,
      saveChangesToDb
    };
  }
});
</script>

<style scoped>
/* Ensure the modal has proper height constraints */
.excel-viewer-container {
  height: 100%;
  max-height: 500px;
}
</style>
